import { isEmpty, keys, map, omit, pickBy } from "lodash";
import moment from "moment-timezone";
import { Areas, MaestroData, Meals, Menus } from "../types";
import { Restaurants } from "../types/Restaurant";
import { WizUser } from "../types/WizUser";
import { getOpeningTimesAdjusted } from "../utils";
import { roles } from "..";

export function getAIFriendlyServiceData(maestroData: MaestroData, allMenus: Menus, allAreas: Areas, allMeals: Meals) {
  if (isEmpty(maestroData) || isEmpty(allMenus) || isEmpty(allAreas) || isEmpty(allMeals)) return {};

  const { style, openingTimes, seatings } = maestroData;

  // 0. Pre-process allMenus to create courseId -> courseName map
  const courseIdToNameMap: { [courseId: string]: string } = {};
  for (const menuId in allMenus) {
    const menu = allMenus[menuId];
    if (menu.courses) {
      for (const courseId in menu.courses) {
        const course = menu.courses[courseId];
        if (!courseIdToNameMap[courseId]) {
          courseIdToNameMap[courseId] = course.name;
        }
      }
    }
  }

  // 1. Parse seatings
  const parsedSeatings = map(seatings, (seating, seatingId) => {
    const { open: adjustedOpen, close: adjustedClose } = getOpeningTimesAdjusted(seating.open, seating.close);

    const parsedIntervals = {};
    for (const intervalId in seating.intervals) {
      const timeKey = moment(Number(intervalId)).utc().format("HH:mm");
      const interval = seating.intervals[intervalId];

      // Build courses object with course names as keys
      const courses: {
        [courseName: string]: {
          id: string;
          enabled: boolean;
          // maxPax: number;
        };
      } = {};

      if (interval.courseIds) {
        for (const courseId in interval.courseIds) {
          const courseName = courseIdToNameMap[courseId] || courseId;
          courses[courseName] = {
            id: courseId,
            enabled: interval.courseIdsEnabled?.[courseId] ?? false,
            // maxPax: interval.courseIds[courseId],
          };
        }
      }

      // Omit widgets, courseIds, courseIdsEnabled and add courses
      parsedIntervals[timeKey] = {
        ...omit(interval, ["widgets", "courseIds", "courseIdsEnabled", "areaGroups"]),
        courses,
        areaGroups: keys(interval.areaGroups),
      };
    }

    const meal = allMeals[seating.mealId];
    const mealName = meal ? meal.name : undefined;

    return {
      ...seating,
      _key: seatingId,
      open: moment(adjustedOpen).utc().format("HH:mm"),
      close: moment(adjustedClose).utc().format("HH:mm"),
      intervals: parsedIntervals,
      mealName,
    };
  });

  // // 2. Parse limits
  // const parsedLimits: any = {
  //   ...omit(limits, "levels"),
  // };

  // if (limits?.areas) {
  //   parsedLimits.areas = {};
  //   for (const areaId in limits.areas) {
  //     const area = allAreas[areaId];
  //     const areaName = area ? area.name : areaId;
  //     parsedLimits.areas[areaName] = {
  //       ...limits.areas[areaId],
  //       id: areaId,
  //     };
  //   }
  // }

  // 3. Parse openingTimes
  let adjustedCloseTime = Number(moment(openingTimes.close).utc().format("HHmm"));
  adjustedCloseTime += openingTimes.close < openingTimes.open ? 2400 : 0;

  const parsedOpeningTimes = {
    ...openingTimes,
    open: Number(moment(openingTimes.open).utc().format("HHmm")),
    close: adjustedCloseTime,
  };

  // 4. Parse style.availability
  const { availability, menuIds } = style;
  const parsedAvailability = [];

  if (availability) {
    for (const menuId in availability) {
      const menu = allMenus[menuId];
      const menuName = menu ? menu.name : undefined;
      const disabledMenuHeadings: { [menuHeadingId: string]: number[] } = {};

      // Process disabled intervals
      if (availability[menuId].disabledIntervals) {
        for (const intervalId in availability[menuId].disabledIntervals) {
          const intervalTime = moment(Number(intervalId)).utc().format("HHmm");
          let intervalTimeNumber = Number(intervalTime);

          // If interval time is past midnight add 2400
          if (intervalTimeNumber < Number(moment(openingTimes.open).utc().format("HHmm"))) {
            intervalTimeNumber += 2400;
          }

          const menuHeadingIds = availability[menuId].disabledIntervals[intervalId]?.menuHeadingIds;

          if (menuHeadingIds) {
            for (const menuHeadingId in menuHeadingIds) {
              if (!disabledMenuHeadings[menuHeadingId]) {
                disabledMenuHeadings[menuHeadingId] = [];
              }
              disabledMenuHeadings[menuHeadingId].push(intervalTimeNumber);
            }
          }
        }
      }

      parsedAvailability.push({
        id: menuId,
        name: menuName,
        disabledMenuHeadings,
        enabled: menuIds?.[menuId] ?? false,
      });
    }
  }

  return {
    hours: {
      start: parsedOpeningTimes.open,
      end: parsedOpeningTimes.close,
    },
    menus: parsedAvailability,
    seatings: parsedSeatings,
    // limit: parsedLimits,
  };
}

/** Returns list of enabled restaurants that the user have grants against */
export function getAccessibleRestaurants(restaurants: Restaurants, user: WizUser) {
  // System users are unrestricted so they get all ENABLED restaurants
  if (roles.hasRole(user?.customClaims?.roles ?? 0, roles.roles.SYSTEM) !== 0) {
    return pickBy(restaurants, (r) => r.enabled === true) as Restaurants;
  }
  const restaurants1: Restaurants = {};
  for (const restaurantId in restaurants) {
    if (restaurants[restaurantId].enabled === true && !isEmpty(user?.restaurants?.[restaurantId]?.grants)) {
      restaurants1[restaurantId] = restaurants[restaurantId];
    }
  }
  return restaurants1;
}
